import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { api } from "../lib/api";
import { Modal } from "./Modal";
import { useToast } from "./Toast";

type Invite = { url: string; name: string; expires_at: number };

/** Makes a personal invite for one crew member and offers every way to hand it
 * over: a QR code to scan off this screen, copy, the phone's share sheet, or email. */
export function InviteSheet({ person, onClose }: { person: { id: number; name: string }; onClose: () => void }) {
  const toast = useToast();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [invite, setInvite] = useState<Invite | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await api.postJson(`/api/players/${person.id}/invite`, {});
      const body = await res.json().catch(() => ({}));
      if (cancelled) return;
      if (!res.ok) { setError(body.error || "Could not make an invite."); return; }
      setInvite({ url: `${window.location.origin}${body.path}`, name: body.name, expires_at: body.expires_at });
    })();
    return () => { cancelled = true; };
  }, [person.id]);

  useEffect(() => {
    if (invite && canvasRef.current) void QRCode.toCanvas(canvasRef.current, invite.url, { width: 240, margin: 1 });
  }, [invite]);

  const message = invite ? `Hi ${invite.name}, here's your personal Trim Log link. Open it on your phone to sign in: ${invite.url}` : "";

  async function copy() {
    if (!invite) return;
    try { await navigator.clipboard.writeText(invite.url); toast("Invite link copied", "ok"); }
    catch { toast("Copy didn't work. Press and hold the link to copy it.", "err"); }
  }
  async function share() {
    if (!invite) return;
    try { await navigator.share({ title: "Your Trim Log invite", text: message }); } catch { /* closed the share sheet */ }
  }

  return <Modal title={`Invite ${person.name}`} onClose={onClose}>
    {error && <p role="alert">{error}</p>}
    {!invite && !error && <p className="muted">Making a link…</p>}
    {invite && <div className="invite-sheet">
      <canvas ref={canvasRef} className="invite-qr" aria-label={`QR code for ${invite.name}'s invite`} />
      <p className="muted small">{invite.name} scans this with their phone camera. It works once and expires on {new Date(invite.expires_at * 1000).toLocaleDateString()}.</p>
      <div className="join-url" data-testid="invite-url">{invite.url}</div>
      <div className="btn-row" style={{ flexWrap: "wrap" }}>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => void copy()}>Copy link</button>
        {typeof navigator.share === "function" && <button type="button" className="btn btn-ghost btn-sm" onClick={() => void share()}>Share</button>}
        <a className="btn btn-ghost btn-sm" href={`mailto:?subject=${encodeURIComponent("Your Trim Log invite")}&body=${encodeURIComponent(message)}`}>Email</a>
      </div>
      <button type="button" className="btn btn-primary" onClick={onClose}>Done</button>
    </div>}
  </Modal>;
}
